import { useApolloClient } from "@apollo/client";
import { MenuItemWithIcon, ModalOpenButton } from "@msys/ui";
import { PlaylistAddOutlined as PlaylistAddOutlinedIcon } from "@mui/icons-material";
import { useTranslate } from "@tolgee/react";
import { useSnackbar } from "notistack";
import React from "react";
import { ConfirmModal } from "../../commons/modals/ConfirmModal.js";
import { useCreateProductListFromQuoteSectionMutation } from "./QuoteItemActions.generated.js";

/**
 * Creates a product list from a quote `section` / `decision` item's leaf
 * products (named after the item), after a confirmation. Gated `MANAGE_PIM` +
 * `PIM` by the caller.
 */
export const CreateProductListFromSectionMenuItem = ({
  projectId,
  docId,
  itemId,
}: {
  projectId: string;
  docId: string;
  itemId: string;
}) => {
  const { t } = useTranslate(["ProductLists", "Global"]);
  const { enqueueSnackbar } = useSnackbar();
  const client = useApolloClient();

  const [createProductListFromQuoteSection] =
    useCreateProductListFromQuoteSectionMutation({ client });

  return (
    <ModalOpenButton
      Modal={ConfirmModal}
      modalProps={{
        title: t("Create product list", { ns: "ProductLists" }),
        text: t(
          "The product list will contain all products from this section.",
          { ns: "ProductLists" }
        ),
        confirmButtonLabel: t("Create", { ns: "Global" }),
        handleConfirm: async () => {
          const result = await createProductListFromQuoteSection({
            variables: { input: { projectId, docId, itemId } },
          });
          if (result.data?.createProductListFromQuoteSection.productList.id) {
            enqueueSnackbar(t("Product list created", { ns: "ProductLists" }), {
              variant: "success",
            });
          }
        },
      }}
    >
      <MenuItemWithIcon icon={<PlaylistAddOutlinedIcon />}>
        {t("Create product list", { ns: "ProductLists" })}
      </MenuItemWithIcon>
    </ModalOpenButton>
  );
};
